/*
 * DiveCatalog — Delivery Promise Engine, one-line promises for cart items
 * AND collection product cards.
 *
 * Placeholders (emitted by cart-items.liquid and price.liquid card contexts):
 *   <div data-dcdp-cart-line data-stamp="…" data-cart-total="123.45"></div>
 *   <div data-dcdp-card-line data-stamp="…" data-price="49.95"></div>
 *
 * Collection cards AND cart/drawer lines render a mini version of the PDP
 * block — icon rows with "Pick up in store <b>Tomorrow, Jul 31</b>" and
 * "Get it by <b>Saturday, Aug 1</b> • FREE". Made-to-order keeps its plain
 * italic range line.
 *
 * Results are cached in sessionStorage (30 min) so collection browsing costs
 * almost nothing. Same rules as delivery-promise.js: fail soft, DOM built node
 * by node, only the ZIP ever leaves the page.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) return;
  if (window.__dcdpCartInit) return;
  window.__dcdpCartInit = true;

  var ENDPOINT = 'https://ycobmooadlvzkbqawzye.supabase.co/functions/v1/delivery-promise';
  var GEO_ENDPOINT = 'https://ipapi.co/json/';
  var LS_ZIP_KEY = 'dc_promise_zip';
  var SS_GUESS_KEY = 'dc_promise_zip_guess';
  var SS_CACHE_PREFIX = 'dcdp_r1|';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  var ZIP_RE = /^\d{5}(-\d{4})?$/;
  var API_TIMEOUT_MS = 3000;
  var GEO_TIMEOUT_MS = 1500;

  function debug() {
    try {
      if (window.console && console.debug) console.debug.apply(console, arguments);
    } catch (e) { /* noop */ }
  }

  function storageGet(storage, key) {
    try { return window[storage].getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* noop */ }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* noop */ }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function isZip(value) {
    return typeof value === 'string' && ZIP_RE.test(value.trim());
  }

  var DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /** "2026-08-06" -> "Thu, Aug 6" (UTC-noon parse avoids timezone day-shift). */
  function formatShortDate(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var d = new Date(iso.slice(0, 10) + 'T12:00:00Z');
    if (isNaN(d.getTime())) return '';
    return DOW_SHORT[d.getUTCDay()] + ', ' + MON_SHORT[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }

  /**
   * Date label, same rules as the PDP block: always a day word PLUS the
   * date — "Today, Jul 30" / "Tomorrow, Jul 31" / "Saturday, Aug 1" /
   * "Friday, Aug 7" — from the shopper's local calendar.
   */
  function dayLabel(y, mIdx, d) {
    var now = new Date();
    var diff = Math.round((Date.UTC(y, mIdx, d) -
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 864e5);
    var dateTxt = MON_SHORT[mIdx] + ' ' + d;
    if (diff === 0) return 'Today, ' + dateTxt;
    if (diff === 1) return 'Tomorrow, ' + dateTxt;
    return DOW_FULL[new Date(Date.UTC(y, mIdx, d)).getUTCDay()] + ', ' + dateTxt;
  }

  function relLabel(iso) {
    if (typeof iso !== 'string' || iso.length < 10) return '';
    var y = Number(iso.slice(0, 4)), m = Number(iso.slice(5, 7)), d = Number(iso.slice(8, 10));
    if (!y || !m || !d) return '';
    return dayLabel(y, m - 1, d);
  }

  /** "on Friday" -> "Tomorrow, Jul 31"; "today by 6pm" passes through. */
  function pickupLabel(text) {
    if (typeof text !== 'string' || !text) return '';
    if (text.indexOf('on ') !== 0) return text;
    var name = text.slice(3);
    var idx = DOW_FULL.indexOf(name);
    if (idx < 0) return name;
    var now = new Date();
    for (var i = 1; i <= 7; i++) {
      var cand = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
      if (cand.getDay() === idx) {
        return dayLabel(cand.getFullYear(), cand.getMonth(), cand.getDate());
      }
    }
    return name;
  }

  /* Same outline icons as the PDP block, sized for cards. Constant markup
     only — never interpolated with API or user data. */
  var CARD_ICONS = {
    store: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 9.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5"/><path d="M4.6 3h14.8L21 7.2a2.3 2.3 0 0 1-4.5.6A2.3 2.3 0 0 1 12 7.6 2.3 2.3 0 0 1 7.5 7.8 2.3 2.3 0 0 1 3 7.2L4.6 3z"/><path d="M9.5 20v-5.5h5V20"/></svg>',
    box: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21 7.5 12 3 3 7.5l9 4.5 9-4.5z"/><path d="M3 7.5v9L12 21l9-4.5v-9"/><path d="M12 12v9"/><path d="M7.5 5.25l9 4.5"/></svg>'
  };

  function cardIcon(name) {
    var holder = document.createElement('span');
    holder.className = 'dcdp-cicon';
    holder.innerHTML = CARD_ICONS[name] || '';
    return holder;
  }

  /* ------------------------------------------- offline service matrix (v1)
     KEEP IN SYNC: this block is copied verbatim into delivery-promise.js and
     delivery-promise-cart.js (delivery-promise/build.py does the copy).

     When the edge function cannot answer (the Supabase project was
     restricted: HTTP 402 exceed_egress_quota 2026-09-24, exceed_db_size_quota
     2026-09-30 — even the CORS preflight fails), in-store stock at Irvine
     still gets a promise in the engine's own result shape, computed from the
     engine's service matrix (config v22, probed 2026-09-23/24 for every state
     at $20-$2,000; same rules as the Tapcart DC Cart Items v3.1 block, plus
     the $20 free-Standard edge of Shopify's General profile in CA / W1).
     Anything the matrix cannot answer returns null -> static fallback line:
     other stamp modes (dropship / jit / mcf / made_to_order need the engine's
     transit tables), heavy / oversized items (own checkout rate tables, e.g.
     Heavy Items CA Standard $22.99 under $150 — never claim FREE for them),
     an unknown ZIP3, or a carrier holiday in the window. */
  var DCDP_OFFLINE = (function () {
    var IRVINE_LOCATION_ID = '10513514611';
    var CUTOFF_HOUR = 14;            // ships same day when ordered by 2pm PT, Mon-Fri
    var PICKUP_UNTIL_HOUR = 18;      // store pickup "today by 6pm", Mon-Fri
    var FREE_STANDARD_FROM = 50;
    var SUB = 'order within {countdown}';
    var DOWS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var DOWL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // ZIP3 ranges -> zone (engine config v22)
    var W1 = [[832, 838], [840, 847], [850, 865], [889, 898], [970, 994]];                // ID UT AZ NV OR WA
    var W2 = [[570, 577], [580, 588], [590, 599], [660, 679], [680, 693], [730, 749],
              [750, 799], [800, 816], [820, 831], [870, 884], [885, 885]];              // SD ND MT KS NE OK TX CO WY NM
    var C6 = [[386, 397], [500, 528], [530, 549], [550, 567], [600, 629], [630, 658],
              [700, 714], [716, 729]];                                                  // MS IA WI MN IL MO LA AR
    var REMOTE = [[6, 9], [967, 968], [995, 999]];                                     // PR / VI, HI, AK
    var MIL = [[90, 99], [962, 966], [969, 969]];                                      // APO / FPO, Guam
    // Carrier holidays: the matrix has no holiday calendar, so a window that
    // touches one falls back to the static line instead of over-promising.
    // Observed dates; extend before 2029.
    var HOLIDAYS = {
      '2026-11-26': 1, '2026-12-25': 1, '2027-01-01': 1, '2027-05-31': 1,
      '2027-07-05': 1, '2027-09-06': 1, '2027-11-25': 1, '2027-12-24': 1,
      '2027-12-31': 1, '2028-05-29': 1, '2028-07-04': 1, '2028-09-04': 1,
      '2028-11-23': 1, '2028-12-25': 1, '2029-01-01': 1
    };

    function inRanges(z, ranges) {
      for (var i = 0; i < ranges.length; i++) {
        if (z >= ranges[i][0] && z <= ranges[i][1]) return true;
      }
      return false;
    }

    function zoneFor(zip) {
      var z = parseInt(String(zip || '').slice(0, 3), 10);
      if (isNaN(z)) return null;
      if (z >= 900 && z <= 961) return { days: 2, freeStd: 20, nd: 38.99, ca: true };
      if (inRanges(z, MIL)) return { days: 7, mil: true };
      if (inRanges(z, REMOTE)) return { days: 14, remote: true };
      if (inRanges(z, W1)) return { days: 4, freeStd: 20, nd: 41.99 };
      if (inRanges(z, W2)) return { days: 4, nd: 43.99 };
      if (inRanges(z, C6)) return { days: 6, slowDays: 7, nd: 45.99 };
      if (z >= 5 && z <= 499) return { days: 7, nd: 47.99 };
      return null;
    }

    /** Wall-clock parts in Los Angeles (DST aware) for an instant. */
    function laParts(ms) {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles', hour12: false, weekday: 'short',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      }).formatToParts(new Date(ms));
      var out = {};
      for (var i = 0; i < parts.length; i++) out[parts[i].type] = parts[i].value;
      return {
        y: Number(out.year), m: Number(out.month) - 1, d: Number(out.day),
        h: Number(out.hour) % 24, mi: Number(out.minute), s: Number(out.second), // "24" = midnight on old Chrome
        dow: DOWS.indexOf(out.weekday)
      };
    }

    function day(y, m, d) {
      var t = new Date(Date.UTC(y, m, d));
      return { y: t.getUTCFullYear(), m: t.getUTCMonth(), d: t.getUTCDate(), dow: t.getUTCDay() };
    }
    function addDays(t, n) { return day(t.y, t.m, t.d + n); }
    function dayNum(t) { return Math.round(Date.UTC(t.y, t.m, t.d) / 864e5); }
    function isBiz(t) { return t.dow !== 0 && t.dow !== 6; }
    function nextBiz(t) {
      var x = addDays(t, 1);
      while (!isBiz(x)) x = addDays(x, 1);
      return x;
    }
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function iso(t) { return t.y + '-' + pad(t.m + 1) + '-' + pad(t.d); }
    function shortDate(t) { return DOWS[t.dow] + ', ' + MONS[t.m] + ' ' + t.d; }

    /** UTC ISO instant of <hour>:00 Los Angeles time on day t. */
    function laWallToIso(t, hour) {
      var target = Date.UTC(t.y, t.m, t.d, hour);
      var guess = target;
      for (var i = 0; i < 2; i++) {
        var p = laParts(guess);
        guess = target - (Date.UTC(p.y, p.m, p.d, p.h, p.mi, p.s) - guess);
      }
      return new Date(guess).toISOString();
    }

    function remoteExpedited(price) {
      return price >= 500 ? 45 : price >= 250 ? 50 : price >= 50 ? 60 : 80;
    }

    /**
     * Engine-shaped result ({tier, mode, ship_by, deliver_by, countdown_ends,
     * pickup, display, transit_source, service, fastest}) or null.
     */
    function promise(stamp, zip, cartValue, nowMs) {
      if (!stamp || typeof stamp !== 'object' || stamp.mode !== 'in_store' ||
          String(stamp.location_id) !== IRVINE_LOCATION_ID || stamp.cls) return null;
      if (typeof zip !== 'string' || !/^\d{5}(-\d{4})?$/.test(zip.trim())) return null;
      var zone = zoneFor(zip.trim());
      if (!zone) return null;
      var price = (typeof cartValue === 'number' && isFinite(cartValue) && cartValue >= 0) ? cartValue : 0;

      var now = laParts(typeof nowMs === 'number' ? nowMs : Date.now());
      if (!(now.dow >= 0) || !now.y) return null;
      var hours = now.h + now.mi / 60 + now.s / 3600;
      var today = day(now.y, now.m, now.d);
      var ship = (isBiz(today) && hours < CUTOFF_HOUR) ? today : nextBiz(today);
      // Next Day lands on Saturday for a Friday ship; only Sunday rolls to Monday.
      var nextDay = addDays(ship, 1);
      if (nextDay.dow === 0) nextDay = addDays(nextDay, 1);

      var result = {
        tier: 'firm',
        mode: 'in_store',
        ship_by: iso(ship),
        deliver_by: null,
        countdown_ends: laWallToIso(ship, CUTOFF_HOUR),
        pickup: {
          available: true,
          text: (isBiz(today) && hours < PICKUP_UNTIL_HOUR) ? 'today by 6pm' : 'on ' + DOWL[nextBiz(today).dow]
        },
        display: { headline: '', sub: SUB, checkout_suffix: '' },
        transit_source: 'offline',
        service: null,
        fastest: null
      };

      var freeNextDayFrom = zone.ca ? 150 : zone.remote ? 1000 : zone.mil ? Infinity : 300;
      var deliver;
      if (price >= freeNextDayFrom) {
        deliver = nextDay;
        result.service = { label: 'Free Next Day Shipping', days: 1, free: true };
        var overnight = dayNum(nextDay) - dayNum(ship) === 1;
        result.display.headline = (overnight ? '⚡ Overnight by ' : '🚚 Get it by ') + shortDate(nextDay);
        result.display.checkout_suffix = (overnight ? ' — overnight by ' : ' — get it by ') + shortDate(nextDay);
      } else {
        var days = (zone.slowDays && price < FREE_STANDARD_FROM) ? zone.slowDays : zone.days;
        deliver = addDays(ship, days);
        if (deliver.dow === 6) deliver = addDays(deliver, 2);
        else if (deliver.dow === 0) deliver = addDays(deliver, 1);
        var freeFrom = zone.remote ? 600 : (zone.freeStd != null ? zone.freeStd : FREE_STANDARD_FROM);
        // APO / FPO / Guam: the engine answers with its default transit and no service.
        result.service = zone.mil ? null : { label: 'Standard Shipping', days: days, free: price >= freeFrom };
        result.display.headline = '🚚 Get it by ' + shortDate(deliver);
        result.display.checkout_suffix = ' — get it by ' + shortDate(deliver);
        if (zone.remote) {
          var exp = ship;
          for (var i = 0; i < 8; i++) exp = nextBiz(exp);
          result.fastest = { label: 'Expedited Shipping', days: 8, price: remoteExpedited(price), deliver_by: iso(exp) };
        } else if (!zone.mil) {
          result.fastest = { label: 'Next Day Shipping', days: 1, price: zone.nd, deliver_by: iso(nextDay) };
        }
      }
      result.deliver_by = iso(deliver);

      var last = deliver;
      if (result.fastest) {
        var f = result.fastest.deliver_by;
        var fd = day(Number(f.slice(0, 4)), Number(f.slice(5, 7)) - 1, Number(f.slice(8, 10)));
        if (dayNum(fd) > dayNum(last)) last = fd;
      }
      for (var x = today; dayNum(x) <= dayNum(last); x = addDays(x, 1)) {
        if (HOLIDAYS[iso(x)]) return null;
      }
      return result;
    }

    return { promise: promise };
  })();

  /* Circuit breaker shared by the PDP block and the card / cart lines: after a
     failed lookup the engine is skipped for a few minutes, so every page view
     renders the offline promise at once instead of waiting on a dead host. */
  var DCDP_DOWN_KEY = 'dcdp_engine_down_until';
  var DCDP_DOWN_MS = 5 * 60 * 1000;
  function engineDown() {
    try {
      var until = Number(window.sessionStorage.getItem(DCDP_DOWN_KEY));
      return isFinite(until) && until > Date.now();
    } catch (e) { return false; }
  }
  function markEngineDown() {
    try { window.sessionStorage.setItem(DCDP_DOWN_KEY, String(Date.now() + DCDP_DOWN_MS)); } catch (e) { /* noop */ }
  }
  function markEngineUp() {
    try { window.sessionStorage.removeItem(DCDP_DOWN_KEY); } catch (e) { /* noop */ }
  }
  /* Only an unreachable engine trips the breaker: network / CORS / timeout
     errors (no status), 402 (Supabase restriction), 408, 429 and 5xx. Any
     other 4xx is a rejected payload and keeps the old static behaviour. */
  function isEngineOutage(err) {
    var status = err && err.status;
    return !status || status === 402 || status === 408 || status === 429 || status >= 500;
  }
  /* ------------------------------------------ end offline service matrix */

  /** Offline-matrix answer for one lookup; null when it cannot answer. */
  function offlineResult(zip, stamp, cartValue) {
    try {
      return DCDP_OFFLINE.promise(stamp, zip, cartValue, Date.now());
    } catch (e) {
      debug('[dcdp-lines] offline matrix failed', e);
      return null;
    }
  }

  /* A fresh sessionStorage entry, or null. The DC Bundles app script
     (dc-bundle-promise.js) shares this cache namespace and stores r:null when
     ITS lookup fails. A null this script wrote (src 'dcdp') is a real engine
     verdict and stays a hit; any other null for a stamp the offline matrix can
     answer is treated as a miss, or the line stays blank for the whole TTL. */
  function freshCacheEntry(key, zip, stamp, cartValue) {
    var cached = storageGet('sessionStorage', SS_CACHE_PREFIX + key);
    if (!cached) return null;
    try {
      var entry = JSON.parse(cached);
      if (!entry || !((Date.now() - entry.t) < CACHE_TTL_MS)) return null;
      if (entry.r === null && entry.src !== 'dcdp' && offlineResult(zip, stamp, cartValue)) return null;
      return entry;
    } catch (e) {
      return null;
    }
  }

  /* In-page only (never sessionStorage), so real engine answers take over
     as soon as the engine is back. */
  function fillOffline(zip, misses) {
    for (var i = 0; i < misses.length; i++) {
      resultCache[misses[i].key] = Promise.resolve(offlineResult(zip, misses[i].stamp, misses[i].cartValue));
    }
  }

  function fetchJSON(url, options, timeoutMs) {
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = null;
    var opts = options || {};
    if (controller) {
      opts = Object.assign({}, opts, { signal: controller.signal });
      timer = window.setTimeout(function () {
        try { controller.abort(); } catch (e) { /* noop */ }
      }, timeoutMs);
    }
    return fetch(url, opts).then(function (res) {
      if (!res.ok) {
        var err = new Error('http_' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (json) {
      if (timer) window.clearTimeout(timer);
      return json;
    }, function (err) {
      if (timer) window.clearTimeout(timer);
      throw err;
    });
  }

  /* Same acquisition order as the PDP block, minus the manual prompt — lines
     stay quiet when no ZIP is known rather than nagging per item. */
  var zipPromise = null;
  function resolveZip() {
    if (zipPromise) return zipPromise;
    zipPromise = Promise.resolve().then(function () {
      var saved = (storageGet('localStorage', LS_ZIP_KEY) || '').trim();
      if (isZip(saved)) return saved;
      var guessed = (storageGet('sessionStorage', SS_GUESS_KEY) || '').trim();
      if (isZip(guessed)) return guessed;
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.analyticsProcessingAllowed !== 'function' ||
            cp.analyticsProcessingAllowed() !== true) {
          return null; // no consent signal -> no geo-IP guess
        }
      } catch (e) {
        return null;
      }
      return fetchJSON(GEO_ENDPOINT, { method: 'GET', mode: 'cors' }, GEO_TIMEOUT_MS)
        .then(function (data) {
          var postal = data && (data.postal || data.zip || data.postal_code);
          postal = typeof postal === 'string' ? postal.trim() : '';
          if (!isZip(postal)) return null;
          ssSet(SS_GUESS_KEY, postal);
          return postal;
        })
        .catch(function () { return null; });
    }).catch(function () { return null; });
    return zipPromise;
  }

  /* The engine only decides on price BRACKETS, so exact prices are
     over-specific cache keys: 24 cards -> 24 API calls. Snapping the value
     DOWN to the union of every real threshold edge in the rate tables gives
     an IDENTICAL promise while collapsing a collection page to ~2-4 calls.
     (500.01 exists because the 68D direct-vs-JIT rule is strictly >$500.) */
  var PRICE_EDGES = [1000, 900, 800, 600, 500.01, 500, 300, 250, 200, 150, 100, 50, 30, 25, 20, 15, 10, 0];
  function quantize(value) {
    if (typeof value !== 'number' || !isFinite(value) || value < 0) return value;
    for (var i = 0; i < PRICE_EDGES.length; i++) {
      if (value >= PRICE_EDGES[i]) return PRICE_EDGES[i];
    }
    return 0;
  }

  /* One lookup per DISTINCT stamp+price-bracket, memoised in-page AND in
     sessionStorage so collection pages barely touch the network. */
  var resultCache = {};

  /** The bold-date shipping sentence shared by cards and cart lines. */
  function shipSentence(container, result, rel, overnight) {
    var prefix = document.createElement('span');
    prefix.className = 'dcdp-headline-prefix';
    prefix.textContent = (overnight ? '⚡ ' : '') +
      (result.tier === 'firm' ? 'Get it by ' : 'Estimated delivery by ');
    var date = document.createElement('strong');
    date.className = 'dcdp-headline-date';
    date.textContent = rel;
    container.appendChild(prefix);
    container.appendChild(date);
    if (result.tier === 'firm' && result.service && result.service.free) {
      container.appendChild(document.createTextNode(' • '));
      var free = document.createElement('strong');
      free.className = 'dcdp-free';
      free.textContent = 'FREE';
      container.appendChild(free);
    }
  }

  /* Cards, cart page and drawer all get the mini PDP block (icon rows
     incl. pickup); made-to-order/dateless results keep the compact line. */
  function renderLine(node, result) {
    while (node.firstChild) node.removeChild(node.firstChild);
    if (!result) return;
    var display = result.display || {};
    var headline = typeof display.headline === 'string' ? display.headline : '';

    // The core only emits the Overnight headline for true next-day arrival.
    var overnight = result.tier === 'firm' && headline.indexOf('Overnight') !== -1;
    var usedBlock = result.tier !== 'range' && !!(result.deliver_by);
    var rel = result.deliver_by ? relLabel(result.deliver_by) : '';

    if (usedBlock && rel) {
      var wrap = document.createElement('span');
      wrap.className = 'dcdp-card-promise';

      // Pickup on any tier: Irvine stock is pickable even when shipping is
      // promised from elsewhere (slow-ship oversized freight, 2026-10-22).
      if (result.pickup && result.pickup.available &&
          typeof result.pickup.text === 'string' && result.pickup.text) {
        var prow = document.createElement('span');
        prow.className = 'dcdp-crow dcdp-crow--pickup';
        prow.appendChild(cardIcon('store'));
        var pbody = document.createElement('span');
        pbody.appendChild(document.createTextNode('Pick up in store '));
        var pstrong = document.createElement('strong');
        pstrong.textContent = pickupLabel(result.pickup.text);
        pbody.appendChild(pstrong);
        prow.appendChild(pbody);
        wrap.appendChild(prow);
      }

      var srow = document.createElement('span');
      srow.className = 'dcdp-crow dcdp-crow--ship' +
        (overnight ? ' dcdp-crow--overnight' : '') +
        (result.tier === 'estimated' ? ' dcdp-crow--estimated' : '');
      srow.appendChild(cardIcon('box'));
      var sbody = document.createElement('span');
      shipSentence(sbody, result, rel, overnight);
      srow.appendChild(sbody);
      wrap.appendChild(srow);
      node.appendChild(wrap);
    } else {
      var line = document.createElement('span');
      line.className = 'dcdp-cart-promise dcdp-cart-promise--' + result.tier +
        (overnight ? ' dcdp-cart-promise--overnight' : '');
      if (result.tier === 'range' || !rel) {
        // Made-to-order (and anything dateless) keeps the config headline.
        if (!headline) return;
        line.textContent = headline;
      } else {
        shipSentence(line, result, rel, overnight);
      }
      node.appendChild(line);
    }

    // Compact paid-upgrade hint: "⚡ As soon as Tomorrow, Jul 31".
    if (result.fastest && result.fastest.deliver_by) {
      var fastRel = relLabel(result.fastest.deliver_by) ||
        formatShortDate(result.fastest.deliver_by);
      if (fastRel) {
        var fastLine = document.createElement('span');
        fastLine.className = 'dcdp-cart-fastest' + (usedBlock && rel ? ' dcdp-cart-fastest--card' : '');
        fastLine.textContent = '⚡ As soon as ' + fastRel;
        node.appendChild(fastLine);
      }
    }
    node.setAttribute('data-dcdp-done', '1');
  }

  /* All of a page's distinct lookups travel in ONE batch request — the
     per-call ~1s round trip was the whole reason collections felt slow. */
  function batchFetch(zip, misses) {
    if (!misses.length) return Promise.resolve();
    if (engineDown()) {
      fillOffline(zip, misses);
      return Promise.resolve();
    }
    var entries = misses.slice(0, 30);
    return fetchJSON(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        zip: zip,
        batch: entries.map(function (m) { return { stamp: m.stamp, cart_value: m.cartValue }; }),
      })
    }, 8000).then(function (data) {
      markEngineUp();
      var results = (data && data.ok === true && data.results) || [];
      for (var i = 0; i < entries.length; i++) {
        var result = results[i] || null;
        if (result && (!result.tier || result.tier === 'none')) result = null;
        resultCache[entries[i].key] = Promise.resolve(result);
        ssSet(SS_CACHE_PREFIX + entries[i].key, JSON.stringify({ t: Date.now(), r: result, src: 'dcdp' }));
      }
      if (misses.length > 30) return batchFetch(zip, misses.slice(30));
    }).catch(function (err) {
      if (!isEngineOutage(err)) {                 // a rejected payload: old behaviour
        debug('[dcdp-lines] batch rejected', err && err.message);
        entries.forEach(function (m) { resultCache[m.key] = Promise.resolve(null); });
        return;
      }
      debug('[dcdp-lines] engine unreachable — offline matrix', err && err.message);
      markEngineDown();
      fillOffline(zip, misses);   // every remaining miss, not just this chunk
    });
  }

  /* ---- ZIP bar: collection / home / search / cart -----------------------
     The PDP has always had its own ZIP prompt, but card lines only ever
     resolved a ZIP silently (saved value -> geo-IP guess, and the guess
     needs analytics consent). A first-time visitor who declines cookies
     therefore saw NO dates anywhere outside a product page (Jason,
     2026-08-04). This mounts ONE bar above the product grid — a prompt when
     no ZIP is known, "Delivery dates for 92614 - Change" once it is — and
     writes the same localStorage key the PDP reads, so the shopper enters it
     once for the whole site. */
  var ZIPBAR_FLAG = '__dcdpZipBar';
  var zipBarState = null;   // 'prompt' | '<zip>' — guards against re-render while typing
  var currentZip = null;

  /** Smallest ancestor holding 2+ placeholders = the grid; else the card itself. */
  function gridAnchor() {
    var first = document.querySelector('[data-dcdp-card-line]') ||
                document.querySelector('[data-dcdp-cart-line]');
    if (!first) return null;
    var node = first;
    for (var hops = 0; hops < 8; hops++) {
      node = node.parentNode;
      if (!node || node === document.body || !node.parentNode) break;
      if (node.querySelectorAll &&
          node.querySelectorAll('[data-dcdp-card-line],[data-dcdp-cart-line]').length >= 2) {
        return node;
      }
    }
    return (first.parentNode && first.parentNode.parentNode) ? first.parentNode : null;
  }

  /** Re-run every line against a newly supplied ZIP. */
  function applyNewZip(value) {
    currentZip = value;
    zipPromise = Promise.resolve(value);        // replace the memoised resolution
    var stale = document.querySelectorAll('[data-dcdp-card-line],[data-dcdp-cart-line]');
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('data-dcdp-done');
      while (stale[i].firstChild) stale[i].removeChild(stale[i].firstChild);
    }
    paintZipBar(value);
    renderAll();
  }

  function paintZipBar(zip) {
    var bar = document.getElementById('dcdp-zipbar');
    if (!bar) return;
    while (bar.firstChild) bar.removeChild(bar.firstChild);
    bar.appendChild(el('span', 'dcdp-zipbar-pin', '📍'));

    if (isZip(zip)) {
      zipBarState = zip;
      var line = el('span', 'dcdp-zipbar-text');
      line.appendChild(document.createTextNode('Delivery dates for '));
      line.appendChild(el('strong', null, zip));
      bar.appendChild(line);
      var change = el('button', 'dcdp-link', 'Change');
      change.type = 'button';
      change.addEventListener('click', function () { paintZipBar(null); });
      bar.appendChild(change);
      return;
    }

    zipBarState = 'prompt';
    bar.appendChild(el('span', 'dcdp-zipbar-text', 'Enter your ZIP code to see delivery dates'));
    var input = el('input', 'dcdp-input');
    input.type = 'text';
    input.autocomplete = 'postal-code';
    input.inputMode = 'numeric';
    input.maxLength = 10;
    input.placeholder = '90210';
    input.setAttribute('aria-label', 'ZIP code');
    var go = el('button', 'dcdp-button', 'Show dates');
    go.type = 'button';
    var error = el('span', 'dcdp-error');
    error.setAttribute('role', 'alert');

    var apply = function () {
      var value = (input.value || '').trim();
      if (!isZip(value)) {                       // validated before anything is sent
        error.textContent = 'Enter a 5-digit ZIP code.';
        input.setAttribute('aria-invalid', 'true');
        return;
      }
      error.textContent = '';
      input.removeAttribute('aria-invalid');
      lsSet(LS_ZIP_KEY, value);
      try {
        window.dispatchEvent(new CustomEvent('dcdp:zipchange', { detail: { zip: value } }));
      } catch (e) { /* noop */ }
      applyNewZip(value);
    };
    go.addEventListener('click', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.keyCode === 13) { e.preventDefault(); apply(); }
    });

    bar.appendChild(input);
    bar.appendChild(go);
    bar.appendChild(error);
    var saved = (storageGet('localStorage', LS_ZIP_KEY) || '').trim();
    if (isZip(saved)) input.value = saved;      // "Change" pre-fills the old value
    if (window[ZIPBAR_FLAG] === 'user') { try { input.focus(); } catch (e) { /* noop */ } }
  }

  function ensureZipBar(zip) {
    try {
      var want = isZip(zip) ? zip : 'prompt';
      if (window[ZIPBAR_FLAG] && zipBarState === want) return;  // never clobber typing
      if (!window[ZIPBAR_FLAG]) {
        var anchor = gridAnchor();
        if (!anchor || !anchor.parentNode) return;
        var bar = el('div', 'dcdp-zipbar');
        bar.id = 'dcdp-zipbar';
        anchor.parentNode.insertBefore(bar, anchor);
        window[ZIPBAR_FLAG] = true;
      }
      currentZip = isZip(zip) ? zip : null;
      paintZipBar(zip);
    } catch (e) { debug('[dcdp-lines] zip bar', e); }
  }

  /* The PDP writes the same key; mirror its change instantly (cart drawer is
     on the PDP too) and pick up other tabs via the storage event. */
  try {
    window.addEventListener('dcdp:zipchange', function (e) {
      var next = e && e.detail && e.detail.zip;
      if (isZip(next) && next !== currentZip) applyNewZip(next);
    });
    window.addEventListener('storage', function (e) {
      if (e && e.key === LS_ZIP_KEY && isZip(e.newValue) && e.newValue !== currentZip) {
        applyNewZip(e.newValue);
      }
    });
  } catch (e) { /* noop */ }

  var rendering = false;
  function renderAll() {
    if (rendering) return;
    var nodes = document.querySelectorAll(
      '[data-dcdp-cart-line]:not([data-dcdp-done]), [data-dcdp-card-line]:not([data-dcdp-done])'
    );
    if (!nodes.length) return;
    rendering = true;
    resolveZip().then(function (zip) {
      ensureZipBar(zip);   // prompt when unknown, "Delivery dates for X" once known
      if (!isZip(zip)) { rendering = false; return; }
      var jobs = [];
      var misses = {};
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var stampRaw = node.getAttribute('data-stamp') || '';
        if (!stampRaw) { node.setAttribute('data-dcdp-done', '1'); continue; }
        // Cart lines price by the CART TOTAL (the whole order ships
        // together); card lines price by the product's own price.
        var raw = node.hasAttribute('data-cart-total')
          ? node.getAttribute('data-cart-total')
          : node.getAttribute('data-price');
        var value = parseFloat(raw || '');
        var cartValue = quantize(isFinite(value) && value >= 0 ? value : null);
        var key = zip + '|' + stampRaw + '|' + cartValue;
        jobs.push({ node: node, key: key });
        if (!resultCache[key]) {
          var stamp = null;
          try { stamp = JSON.parse(stampRaw); } catch (e) { /* bad stamp */ }
          var entry = freshCacheEntry(key, zip, stamp, cartValue);
          var hit = false;
          if (entry) {
            resultCache[key] = Promise.resolve(entry.r);
            hit = true;
          }
          if (!hit && !misses[key]) {
            if (stamp && typeof stamp === 'object' && stamp.location_id != null) {
              misses[key] = { key: key, stamp: stamp, cartValue: cartValue };
            } else {
              resultCache[key] = Promise.resolve(null);
            }
          }
        }
      }
      var missList = Object.keys(misses).map(function (k) { return misses[k]; });
      batchFetch(zip, missList).then(function () {
        jobs.forEach(function (job) {
          (resultCache[job.key] || Promise.resolve(null)).then(function (result) {
            try {
              renderLine(job.node, result);
            } catch (e) { debug('[dcdp-lines] render', e); }
            // processed either way — a null result must not re-queue forever
            job.node.setAttribute('data-dcdp-done', '1');
          });
        });
        rendering = false;
      }, function () { rendering = false; });
    }, function () { rendering = false; });
  }

  /* Cart updates, drawer re-renders, infinite-scroll collection grids — all
     inject fresh placeholders (scripts inside injected HTML never re-run). */
  var debounceTimer = null;
  function scheduleRender() {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(renderAll, 200);
  }

  function init() {
    renderAll();
    try {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleRender();
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (e) {
      debug('[dcdp-lines] observer unavailable', e);
    }
    // The theme fires cart:refresh after drawer/cart updates — belt & braces.
    document.documentElement.addEventListener('cart:refresh', scheduleRender);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
